import React, { useState, useMemo } from 'react';
import { GitMerge, Search, AlertTriangle, Copy, ChevronRight, X } from 'lucide-react';
import { safeAsync, Badge, btnPrimary, btnSm, cardWrap, neuShadow, neuShadowSm, neuShadowInset, inp } from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

export default function DuplicatesTab({ duplicateGroups, loadExtended, loadData }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['high', 'medium', 'low']));
  const [searchTerm, setSearchTerm] = useState('');

  // Group duplicates by confidence level
  const grouped = useMemo(() => {
    const groups = { high: [], medium: [], low: [] };
    duplicateGroups.forEach(g => {
      if (g.confidence >= 90) {
        groups.high.push(g);
      } else if (g.confidence >= 70) {
        groups.medium.push(g);
      } else {
        groups.low.push(g);
      }
    });
    return groups;
  }, [duplicateGroups]);

  // Filter by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return grouped;
    const term = searchTerm.toLowerCase();
    return {
      high: grouped.high.filter(g =>
        matchesNameQuery([g.match_reason], term) ||
        (g.contacts || []).some(c =>
          matchesNameQuery([c.first_name, c.last_name, c.email], term)
        )
      ),
      medium: grouped.medium.filter(g =>
        matchesNameQuery([g.match_reason], term) ||
        (g.contacts || []).some(c =>
          matchesNameQuery([c.first_name, c.last_name, c.email], term)
        )
      ),
      low: grouped.low.filter(g =>
        matchesNameQuery([g.match_reason], term) ||
        (g.contacts || []).some(c =>
          matchesNameQuery([c.first_name, c.last_name, c.email], term)
        )
      ),
    };
  }, [grouped, searchTerm]);

  // Auto-expand on search
  React.useEffect(() => {
    if (searchTerm) {
      setExpandedGroups(new Set(['high', 'medium', 'low']));
    }
  }, [searchTerm]);

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(['high', 'medium', 'low']));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const groupConfigs = [
    { id: 'high', label: 'High Confidence (90%+)', color: '#DC3545' },
    { id: 'medium', label: 'Medium Confidence (70-89%)', color: '#F26522' },
    { id: 'low', label: 'Low Confidence (<70%)', color: '#6C757D' },
  ];

  const totalDuplicates = Object.values(filteredGroups).reduce((sum, g) => sum + g.length, 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <GitMerge size={18} color="#DC3545" /> Duplicate Detection & Merging
        </h3>
        <button onClick={safeAsync(async () => {
          const res = await window.zignagenixAPI.scanDuplicates();
          alert(`Scan complete: ${res.groups} duplicate groups found`);
          loadExtended();
        })} style={btnPrimary}><Search size={13} /> Scan for Duplicates</button>
      </div>

      {/* Summary */}
      <div style={{ ...cardWrap, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <AlertTriangle size={16} color={duplicateGroups.length > 0 ? '#F26522' : '#62BB46'} />
        <span style={{ fontSize: 12, color: '#212529' }}>
          {duplicateGroups.length > 0 ? (
            <><strong>{duplicateGroups.length}</strong> potential duplicate groups found. Review and merge or dismiss below.</>
          ) : (
            <>No pending duplicates. Click "Scan for Duplicates" to check.</>
          )}
        </span>
      </div>

      {duplicateGroups.length === 0 ? null : (
        <>
          {/* Search bar */}
          <div style={{ marginBottom: 16, position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by match reason or contact name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                ...inp,
                paddingRight: 32,
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: 10,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6C757D',
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Expand/Collapse All buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={expandAll} style={btnSm}>Expand All</button>
            <button onClick={collapseAll} style={btnSm}>Collapse All</button>
          </div>

          {/* Accordion groups */}
          {groupConfigs.map(groupConfig => {
            const groups = filteredGroups[groupConfig.id];
            if (groups.length === 0) return null;
            const isExpanded = expandedGroups.has(groupConfig.id);

            return (
              <div key={groupConfig.id} style={{ marginBottom: 12 }}>
                {/* Group header */}
                <button
                  onClick={() => toggleGroup(groupConfig.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    background: '#F8F9FA',
                    border: `1px solid #E9ECEF`,
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#212529',
                    transition: 'background-color 0.2s',
                    boxShadow: neuShadowSm,
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#F1F3F5'}
                  onMouseLeave={(e) => e.target.style.background = '#F8F9FA'}
                >
                  <ChevronRight
                    size={16}
                    style={{
                      color: groupConfig.color,
                      transition: 'transform 0.3s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  />
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <span style={{ color: groupConfig.color, marginRight: 4 }}>●</span>
                    {groupConfig.label}
                  </div>
                  <div
                    style={{
                      background: groupConfig.color,
                      color: 'white',
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 12,
                      minWidth: 24,
                      textAlign: 'center',
                    }}
                  >
                    {groups.length}
                  </div>
                </button>

                {/* Expandable content */}
                <div
                  style={{
                    maxHeight: isExpanded ? '5000px' : '0px',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease-in-out',
                  }}
                >
                  <div
                    style={{
                      background: '#FFFFFF',
                      border: `1px solid #E9ECEF`,
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      boxShadow: neuShadow,
                    }}
                  >
                    {groups.map((g, i) => {
                      const contacts = g.contacts || [];
                      return (
                        <div
                          key={g.id}
                          style={{
                            padding: '16px',
                            borderBottom: i < groups.length - 1 ? '1px solid #E9ECEF' : 'none',
                            background: i % 2 === 1 ? '#F8F9FA' : '#FFFFFF',
                          }}
                        >
                          {/* Confidence and reason */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Badge text={`${g.confidence}% match`} color={groupConfig.color} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{g.match_reason}</span>
                          </div>

                          {/* Contact comparison */}
                          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                            {contacts.map(c => (
                              <div key={c.id} style={{ flex: 1, background: '#F8F9FA', borderRadius: 10, padding: 12, boxShadow: neuShadowInset }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 4 }}>{c.first_name} {c.last_name}</div>
                                <div style={{ fontSize: 10, color: '#6C757D' }}>ID: {c.id}</div>
                                <div style={{ fontSize: 10, color: '#6C757D' }}>Email: {c.email || '—'}</div>
                                <div style={{ fontSize: 10, color: '#6C757D' }}>Phone: {c.phone || '—'}</div>
                                <div style={{ fontSize: 10, color: '#6C757D' }}>Stage: {c.pipeline_stage || '—'}</div>
                                <div style={{ fontSize: 10, color: '#ADB5BD' }}>Created: {new Date(c.created_at).toLocaleDateString()}</div>
                              </div>
                            ))}
                          </div>

                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {contacts.map(c => (
                              <button key={c.id} onClick={safeAsync(async () => {
                                const mergeIds = contacts.filter(x => x.id !== c.id).map(x => x.id);
                                if (!window.confirm(`Keep #${c.id} (${c.first_name} ${c.last_name}) and merge others into it?`)) return;
                                await window.zignagenixAPI.mergeDuplicates({ groupId: g.id, keepId: c.id, mergeIds });
                                alert('Contacts merged!'); loadData(); loadExtended();
                              })} style={btnPrimary}><Copy size={12} /> Keep #{c.id}</button>
                            ))}
                            <button onClick={safeAsync(async () => { await window.zignagenixAPI.dismissDuplicate(g.id); loadExtended(); })}
                              style={btnSm}>Dismiss</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
